import React, {
  Suspense, useState, useCallback, useRef,
} from 'react';
import i18next from 'i18next';
import classNames from 'classnames';

import { sailthruSignUp } from 'lib/sailthruUtils';
import validEmail from 'lib/validEmail';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { NL_SIGNUP_BUTTON_ANIMATED, EMAIL_PLACEHOLDER_TXT } from 'lib/brandFeatures';
import { InView } from 'react-intersection-observer';

import { Button } from 'components/Button';

import getConfig from 'next/config';
import './styles.themed.scss';

const Recaptcha = React.lazy(() => import('reaptcha'));

const { publicRuntimeConfig: { SAILTHRU_CUSTOMER_ID } } = getConfig();
const block = 'nl-signup-input';


/**
 * SignupInput component for newsletter email subscription.
 * This component handles email validation, reCAPTCHA verification, and Sailthru integration
 * for newsletter signups with customizable styling based on context and vertical.
 *
 * @param {object} props - The component props.
 * @param {string} props.newsletterId - The unique identifier for the newsletter.
 * @param {Function} [props.onError] - Callback function called when signup fails.
 * @param {Function} [props.onSubmit] - Callback function called when signup succeeds.
 * @param {JSX.Element} [props.renderOnSuccess] - Custom success message component to render.
 * @param {string} [props.signupSource] - Source identifier for analytics tracking.
 * @param {string} props.sitekey - The reCAPTCHA site key for verification.
 * @param {string} props.vertical - The vertical identifier for styling and features.
 * @param {boolean} [props.isRail=false] - Whether component is in rail/sidebar context.
 * @param {string} [props.newsletterIdSource] - Additional newsletter ID source for tracking.
 * @param {boolean} [props.grayscale=false] - Whether to apply grayscale styling.
 * @returns {JSX.Element} The newsletter signup input component.
 */
const SignupInput = ({
  newsletterId,
  onError = Function.prototype,
  onSubmit = Function.prototype,
  renderOnSuccess = (
    <p>
      {i18next.t('You have been successfully added to our newsletter')}
    </p>
  ),
  signupSource = null,
  sitekey,
  vertical,
  isRail = false,
  newsletterIdSource = null,
  grayscale = false,
}) => {
  const [emailAddress, setEmailAddress] = useState('');
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const formRef = useRef(null);
  const inputBoxRef = useRef(null);
  const recaptchaRef = useRef(null);

  const handleChange = useCallback((e) => {
    const { value } = e.target;
    setEmailAddress(value);
  }, []);


  const handleSubmit = useCallback((e) => {
    e.preventDefault();


    if (validEmail(emailAddress)) {
      inputBoxRef.current?.setCustomValidity('');
      recaptchaRef.current?.execute();
      setEmailInvalid(false);
    } else {
      inputBoxRef.current?.setCustomValidity(i18next.t('Invalid email'));
      formRef.current?.reportValidity();
      setEmailInvalid(true);
    }
  }, [emailAddress]);


  const captchaVerify = useCallback(() => {
    const signupValues = {
      id: emailAddress,
      key: 'email',
      email: emailAddress,
      lists: {
        Master: 1,
      },
      vars: {
        [newsletterId]: 1,
        ...(newsletterIdSource && { [newsletterIdSource]: signupSource }),
        sign_up_date: new Date().toISOString(),
        referrer: document?.referrer,
        domain: document?.location.href,
      },
      source: signupSource,
      /**
       * Success callback for Sailthru signup.
       *
       * @param {object} root0 - The success response object.
       * @param {string} root0.email - The email address that was successfully subscribed.
       */
      onSuccess: ({ email }) => {
        setSubmitted(true);
        onSubmit(email);
      },
      /**
       * Error callback for Sailthru signup.
       */
      onError: () => {
        onError();
      },
    };

    sailthruSignUp(signupValues, SAILTHRU_CUSTOMER_ID);
  }, [emailAddress, newsletterId, signupSource, onSubmit, onError, newsletterIdSource]);


  const placeholderText = i18next.t(getFeatureConfigForBrand(EMAIL_PLACEHOLDER_TXT, vertical));
  const animated = getFeatureConfigForBrand(NL_SIGNUP_BUTTON_ANIMATED, vertical);
  const title = i18next.t('Sign Up');

  return (
    <div className={block} data-testid="newsletter-signup-input">
      <div className={classNames(`${block}__input-wrapper`, {
        [`${block}__input-wrapper--grayscale`]: grayscale,
        [`${block}__input-wrapper--rail`]: isRail,
      })}
      >
        {!submitted ? (
          <form
            className={classNames(`${block}__form`, {
              [`${block}__form--grayscale`]: grayscale,
              [`${block}__form--rail`]: isRail,
            })}
            ref={formRef}
          >
            <input
              className={classNames(
                `${block}__input`,
                {
                  [`${block}__input--grayscale`]: grayscale,
                  [`${block}__input--invalid`]: emailInvalid,
                  [`${block}__input--rail`]: isRail,
                },
              )}
              name="emailAddress"
              onChange={handleChange}
              placeholder={placeholderText}
              ref={inputBoxRef}
              value={emailAddress}
              required="required"
              aria-required="true"
              type="email"
              minLength="3" // https://stackoverflow.com/a/1423203
            />
            <Button
              id="email-subscribe-button"
              displayType="inline-form-button"
              title={title}
              animated={animated}
              onClick={handleSubmit}
              data-confirm_howl="false"
              data-show_prompt="false"
              additionalClasses={classNames(`${block}__button`, {
                [`${block}__button--grayscale`]: grayscale,
                [`${block}__button--rail`]: isRail,
              })}
            />
          </form>
        ) : (
          <div
            className={classNames(
              `${block}__submitted-message`,
              { [`${block}__submitted-message--rail`]: isRail },
            )}
          >
            {React.cloneElement(renderOnSuccess)}
          </div>
        )}
      </div>

      <footer className={classNames(`${block}__footer`, {
        [`${block}__footer--grayscale`]: grayscale,
        [`${block}__footer--rail`]: isRail,
      })}
      >
        {i18next.t('This site is protected by recaptcha')}

        &nbsp;
        <span
          className={classNames(
            `${block}__recaptcha-links`,
            { [`${block}__recaptcha-links--rail`]: isRail },
          )}
        >
          <a
            className={classNames(
              `${block}__recaptcha-link`,
              {
                [`${block}__recaptcha-link--rail`]: isRail,
              },
            )}
            href="https://www.nbcuniversal.com/privacy"
            rel="noopener noreferrer"
            target="_blank"
          >
            {i18next.t('Privacy Policy')}
          </a>
          {isRail && ('\u00A0')}
          &nbsp;|&nbsp;
          {isRail && ('\u00A0')}

          <a
            className={classNames(
              `${block}__recaptcha-link`,
              {
                [`${block}__recaptcha-link--rail`]: isRail,
              },
            )}
            href="https://www.nbcuniversal.com/terms"
            rel="noopener noreferrer"
            target="_blank"
          >
            {i18next.t('Terms of Service')}
          </a>
        </span>
      </footer>
      {sitekey && (
        <InView>
          {({ inView, ref }) => (
            <div ref={ref}>
              {inView && (
                <Suspense fallback={null}>
                  {/*
                  Component internally handles loading the recaptcha SDK.
                  So if we lazy mount the component when the user scrolls into view
                  we can defer downloading the recaptcha sdk
                */}
                  <Recaptcha
                    ref={recaptchaRef}
                    onVerify={captchaVerify}
                    sitekey={sitekey}
                    size="invisible"
                  />
                </Suspense>
              )}
            </div>
          )}
        </InView>
      )}
    </div>
  );
};

export { SignupInput };
